import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { Button, SidebarNav } from '@grovus/design-system'

export interface AppShellNavItem {
  value: string
  label: string
  count?: number
}

interface Props {
  /** Rendered inside the shell's one `<header>` landmark: logo, account trigger, controls. */
  header: ReactNode
  navItems: AppShellNavItem[]
  active: string
  onSelect: (value: string) => void
  /** Accessible name for the primary navigation, already localized. */
  navLabel: string
  /** Label for the below-768px collapsed-navigation toggle, already localized. */
  navToggleLabel: string
  /** Saved views and the reliability legend: sidebar content below the nav proper. */
  sidebarFooter?: ReactNode
  /** The shell-owned h1, already localized for the active screen. */
  headingText: string
  /** A full-width status banner between the header and the body, e.g. sign-out failure. */
  banner?: ReactNode
  /**
   * Suppresses the sidebar and main content entirely, leaving only the header and
   * banner. Used while sign-out is pending or has failed to confirm, so nothing the
   * session loaded stays on screen a moment longer than the sign-out request needs.
   */
  bodyHidden?: boolean
  /** DetailPanel, the account panel and anything else positioned over the shell. */
  overlays?: ReactNode
  /** Below 768px, from App's `useNarrow`: the one answer both the shell and App act on. */
  narrow: boolean
  children?: ReactNode
}

/**
 * Header, sidebar, content and overlay slots on native landmarks: exactly one
 * `header`, one `aside` and one `main`. Below 768px the sidebar becomes a
 * collapsible section in normal document flow rather than a drawer, so it never
 * covers content and needs no focus trap; the toggle owns aria-expanded and
 * aria-controls, and Escape or a selection close it and move focus, in the two
 * places that matter to someone using a keyboard on a narrow screen.
 */
export function AppShell({
  header, navItems, active, onSelect, navLabel, navToggleLabel, sidebarFooter, headingText,
  banner, bodyHidden, overlays, narrow, children,
}: Props) {
  const [navOpen, setNavOpen] = useState(false)
  const headingRef = useRef<HTMLHeadingElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const navId = 'app-shell-nav'
  const toggleId = 'app-shell-nav-toggle'

  // Widening past 768px must not leave the drawer's closed state stranded: the sidebar
  // reads as always-visible there regardless of navOpen (see the `hidden` expression
  // below), but resetting it keeps a later narrow render starting closed, not however
  // the drawer happened to sit the last time the viewport was narrow.
  useEffect(() => {
    if (!narrow) setNavOpen(false)
  }, [narrow])

  // Below 768px the body is the scroller and the Menu section sits at its top, so a Menu
  // opened with the list scrolled down would open out of sight and the toggle would seem
  // to do nothing. Opening it scrolls the body back to the section; focus stays on the
  // toggle, which the header keeps in view.
  useEffect(() => {
    if (narrow && navOpen) bodyRef.current?.scrollTo?.({ top: 0 })
  }, [narrow, navOpen])

  const focusHeading = useCallback(() => headingRef.current?.focus(), [])
  const focusToggle = useCallback(() => document.getElementById(toggleId)?.focus(), [])

  function handleSelect(value: string) {
    onSelect(value)
    if (narrow) {
      setNavOpen(false)
      focusHeading()
    }
  }

  function handleNavKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Escape' || !narrow || !navOpen) return
    event.stopPropagation()
    setNavOpen(false)
    focusToggle()
  }

  return (
    <div className="app-shell">
      <header className="app-shell-header">
        {narrow && !bodyHidden && (
          <Button
            id={toggleId}
            variant="default"
            className="app-shell-nav-toggle"
            aria-expanded={navOpen}
            aria-controls={navId}
            onClick={() => setNavOpen((v) => !v)}
          >
            {navToggleLabel}
          </Button>
        )}
        {header}
      </header>

      {banner}

      {!bodyHidden && (
        <div ref={bodyRef} className="app-shell-body">
          <aside
            id={navId}
            className="app-shell-nav"
            aria-label={navLabel}
            hidden={narrow && !navOpen}
            onKeyDown={handleNavKeyDown}
          >
            <SidebarNav
              items={navItems}
              active={active}
              onSelect={handleSelect}
              ariaLabel={navLabel}
            />
            {sidebarFooter}
          </aside>

          <main className="app-shell-main">
            <h1 ref={headingRef} tabIndex={-1} className="app-shell-heading">{headingText}</h1>
            {children}
          </main>
        </div>
      )}

      {overlays}
    </div>
  )
}
